// One cursor-paging primitive for every `/v1` list (arc 101 slice 06).
//
// Six near-identical copies of "accumulate pages behind a Load more button" had
// grown across the SPA (the repo Audit + Issues tabs, the account audit log, the
// three admin registries), and five more lists dropped `next_cursor` on the floor
// entirely — page one WAS the list, silently. This is the one implementation.
// `lib/paginate.ts` is the different problem next door: walking rows the client
// already holds in full, where a "Load more" would be a lie. The rules the copies
// kept getting wrong are stated where they're enforced below and summarised in
// this app's CLAUDE.md; `paged.test.ts` pins each one.

import { batch, createEffect, createSignal, on, untrack } from 'solid-js';
import { createStore, reconcile, type SetStoreFunction } from 'solid-js/store';
import { activeAccountId } from './active-account';
import { createWindowPoll, type WalkedPage } from './paged-poll';
import { MAX_AUTO_PAGES, type PageSlices, pageSlices } from './paged-slices';
import type { Page, PagedList, PagedOptions, PageQuery } from './paged-types';
import { pagedView } from './paged-view';

export { MAX_AUTO_PAGES } from './paged-slices';
export type {
  Page,
  PagedList,
  PagedOptions,
  PagedPoll,
  PagedView,
  PageQuery,
} from './paged-types';

/**
 * Page a `{ data, next_cursor }` list. `query` receives the cursor accessor and
 * returns the (reactive) TanStack query built at it — so the caller keeps
 * ownership of its filters and query key, and this owns only the paging.
 */
/**
 * The load-more half: advance, or re-read the next page when it failed. The
 * failure is the page asked for — the one the server pointed at — erroring;
 * a failed background re-read of a page already merged is not a load-more
 * failure, and `pagedView` owns the nothing-on-screen case.
 */
function nextPage<T>(
  list: PageQuery<T>,
  cursor: () => string | null,
  nextCursor: () => string | null,
  setCursor: (c: string) => void,
  asked: () => void,
): Pick<PagedList<T>, 'loadMoreFailed' | 'loadMore'> {
  return {
    loadMoreFailed: () => list.isError && cursor() !== null && cursor() === nextCursor(),
    loadMore: () => {
      const next = nextCursor();
      if (next === null || list.isFetching) return;
      asked();
      // Already asked for `next` and it never landed (a 5xx mid-deploy): the
      // cursor is AT it, so `setCursor(next)` re-keys nothing — re-read it.
      if (cursor() === next) void list.refetch();
      else setCursor(next);
    },
  };
}

/**
 * A `reload()`'s forced read, then `settled()` — fresh, or failed and still
 * cached. Off the effect body: `refetch()` writes query state, and re-entering
 * the merge effect from inside itself deadlocks the update cycle. `settled`
 * re-runs the merge so a paused `exhaust` walk resumes from the page one in hand.
 */
function forceRead(list: { readonly refetch: () => unknown }, settled: () => void): void {
  queueMicrotask(() => void Promise.resolve(list.refetch()).finally(settled));
}

/**
 * Swap a poll's re-walked window in whole — slices, rows, next cursor, and the
 * query's key, which stays at the window's LAST page: a later invalidation or
 * load-more retry then refreshes that slice in place, instead of reading a token
 * the window no longer holds and filing it as a page of its own.
 */
function windowLander<T>(to: {
  readonly slices: PageSlices<T>;
  readonly key: string;
  readonly setNextCursor: (c: string | null) => void;
  readonly setRows: SetStoreFunction<T[]>;
  readonly setCursor: (c: string | null) => void;
}): (walked: readonly WalkedPage<T>[]) => void {
  return (walked) => {
    const last = walked.at(-1);
    if (!last) return;
    batch(() => {
      to.slices.clear();
      for (const { at, page } of walked) to.slices.hold(at, page.data);
      to.setNextCursor(last.page.next_cursor);
      to.setRows(reconcile(to.slices.flatten(), { key: to.key }));
      to.setCursor(last.at);
    });
  };
}

export function usePagedQuery<T extends object>(
  query: (cursor: () => string | null) => PageQuery<T>,
  options: PagedOptions<T> = {},
): PagedList<T> {
  const key = options.key ?? 'id';
  const [cursor, setCursor] = createSignal<string | null>(null);
  const [nextCursor, setNextCursor] = createSignal<string | null>(null);
  const [rows, setRows] = createStore<T[]>([]);
  // Bumped by `reset()` from page one, where `setCursor(null)` is a no-op: if the
  // caller's own filter didn't move either (a search box re-submitted with the
  // same text) the query key is unchanged, so `list.data` never changes and the
  // merge below would never re-run — leaving the rows `reset()` just cleared with
  // nothing to refill them. The tick re-runs it against whatever the query holds.
  const [resetTick, setResetTick] = createSignal(0);
  const list = query(cursor);
  const slices = pageSlices<T>();
  /** A `reload()` waiting for the query to actually be back on page one. */
  let reloadPending = false;
  /**
   * The forced page-one read is in flight. An `exhaust` walk waits for it: re-keying
   * to `next_cursor` before it ran moved the refetch onto the LAST page, and page
   * one kept its pre-write rows from cache (#5608 slice 17 row 4).
   */
  let refreshingPageOne = false;
  /** The tenant the held slices were read under — see the boundary below. */
  let heldFor = untrack(activeAccountId);
  /** The last page merged: evidence about the tenant it was read under, only. */
  let merged: Page<T> | undefined;

  const keyOf = (row: T): unknown => (row as Record<string, unknown>)[key];

  const drop = (): void => {
    slices.clear();
    setCursor(null);
    setNextCursor(null);
    setRows(reconcile([] as T[], { key }));
  };

  const reset = (): void =>
    batch(() => {
      const atPageOne = cursor() === null;
      drop();
      // From a deeper page `setCursor(null)` already re-keys the query, which
      // refills. From page one nothing moved unless the caller's own filter did —
      // so re-run the merge and let it decide (a re-keyed query reads `undefined`
      // and leaves the list cleared; an unchanged one refills).
      if (atPageOne) setResetTick((n) => n + 1);
    });

  createEffect(
    on([() => list.data, resetTick, activeAccountId], ([page, , account]) => {
      // THE ACCOUNT BOUNDARY. Every account-scoped caller keys its query on
      // `activeAccountId()`, but the cursor and the slices live HERE — and an org
      // switch NAVIGATES rather than remounting (`/org/a/repos` -> `/org/b/repos`
      // keeps this primitive alive). Without this, org B is read at org A's
      // keyset cursor and org A's rows stay on screen until it answers; worse, a
      // re-keyed query answering from TanStack's cache files org B's page one
      // BEHIND org A's slices.
      if (account !== heldFor) {
        heldFor = account;
        drop();
        // The page still in hand was READ under the tenant we just left, so it
        // is not evidence about this one — a caller whose key carries the
        // account will answer again (or `undefined`) and that answer is page one.
        //
        // `page` is this effect's ARGUMENT, not state: `drop()` clears the
        // cursor, the slices and the rows and cannot reach it. So `merged` is
        // the only value that has to be read before it is cleared — and it is
        // cleared UNCONDITIONALLY, on the stale return as well, because a
        // tenant boundary drops the evidence either way. Reading these two
        // through captured aliases said `drop()` cleared `page`, which it never
        // did, and moved the clear behind the return.
        const stale = page !== undefined && page === merged;
        merged = undefined;
        if (stale) return;
      }
      if (!page) return;
      const at = cursor();
      setNextCursor(page.next_cursor);
      // A fresh page one invalidates every deeper slice. Not because the server's
      // cursors moved (keyset since task #71 — a token names a ROW and stays
      // valid), but because the rows BEHIND them were re-read: keeping the old
      // slices would show a refreshed page one stitched to a stale tail.
      if (at === null) slices.clear();
      slices.hold(at, page.data);
      merged = page;
      setRows(reconcile(slices.flatten(), { key }));
      // A pending `reload()` forces its read HERE, not at the call site: holding a
      // page at cursor `null` is the only proof the query has finished re-keying.
      // Refetching any earlier races that re-key and re-runs the page being left,
      // whose rows then land filed under page one.
      if (reloadPending && at === null) {
        reloadPending = false;
        refreshingPageOne = true;
        return forceRead(list, () => {
          refreshingPageOne = false;
          setResetTick((n) => n + 1);
        });
      }
      const walk = options.exhaust && !refreshingPageOne && page.next_cursor;
      if (walk && slices.count() < MAX_AUTO_PAGES) {
        setCursor(page.next_cursor);
      }
    }),
  );

  const busy = (): boolean => list.isFetching || reloadPending || refreshingPageOne;
  const land = windowLander({ slices, key, setNextCursor, setRows, setCursor });
  createWindowPoll(options.poll, slices, busy, land);

  return {
    rows: () => rows,
    view: pagedView(list, () => rows),
    hasMore: () => nextCursor() !== null,
    isFetching: () => list.isFetching,
    ...nextPage(list, cursor, nextCursor, setCursor, slices.asked),
    reset,
    reload: () => {
      // Page one sits inside the client's 30s `staleTime`, so re-keying to it
      // answers from CACHE — and after a write the cache is exactly what's wrong.
      // The merge above fires the read once the re-key has landed.
      reloadPending = true;
      // Past page one, drop the accumulation too: the held cursors still address
      // real rows, but the write we are reloading for may have moved or removed
      // one, so the deeper pages no longer line up with page one. ON page one,
      // deliberately DON'T — emptying the store unmounts every row, and a queue
      // can be an edit surface (the OSS reviewer note half-typed into a
      // neighbouring row); re-running the merge reconciles it in place instead.
      if (cursor() !== null) reset();
      else setResetTick((n) => n + 1);
    },
    patch: (fresh: T) => {
      const k = keyOf(fresh);
      slices.replace((row) => keyOf(row) === k, fresh);
      const i = rows.findIndex((r) => keyOf(r) === k);
      if (i >= 0) setRows(i, reconcile(fresh, { key }));
    },
  };
}
